import {
  IPayerResponse,
  PayeeTaxDocumentSharePermission,
  PayerPayeeStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  Payer,
  PayerStatus,
  PayerTaxInformationShareStatus
} from "@wingspanhq/wingspan-api-sdk";

/**
 * Adapt a v3 `Payer` into the v1 `IPayerResponse` shape that legacy widgets and
 * prop-drilled components still consume, so they render unchanged while the data
 * source moves to v3. Only company DBA, email, and active/tax-shared status have a
 * v3 source today.
 *
 * The remaining v1 fields are left unset — and per the V3 design (PLA-3198), most
 * are deliberately NOT Payer fields, so consumers must be re-sourced, not wait for
 * the Payer model to grow them:
 * - address / legal name / individual name: live on Account/ComplianceEntity behind
 *   a graduated informationSharing grant (#1) — never duplicated onto Payer.
 * - QuickBooks integration: on v3 AccountingConnection, not Payer (#6).
 * - payerSettings / AutoPay: intentionally cut from the payee Payer surface (#5).
 * - payment-method / bank data: genuine design hole (#4).
 * Consumers reading these degrade to empty; see PLA-3198 for the correct source.
 */
export const selectorPayerResponseFromV3 = (payer: Payer): IPayerResponse => {
  const status =
    payer.status === PayerStatus.ACTIVATED
      ? PayerPayeeStatus.Active
      : PayerPayeeStatus.Inactive;
  const shareTaxDocument =
    payer.taxInformationShare?.status === PayerTaxInformationShareStatus.ACTIVE
      ? PayeeTaxDocumentSharePermission.Allow
      : undefined;
  const doingBusinessAs = payer.profile?.company?.doingBusinessAs;

  return {
    payerId: payer.id,
    user: { email: payer.email },
    member: { profile: { company: { doingBusinessAs } } },
    payeeOwnedData: {
      status,
      shareTaxDocument,
      payerCompanyName: doingBusinessAs,
      // Invoice CC recipients — now a first-class v3 Payer field
      // (`notificationCcEmails`, WIN-15229), so this no longer degrades to empty.
      payerEmailCC: payer.notificationCcEmails
    },
    payerOwnedData: { status }
  } as unknown as IPayerResponse;
};
